import type { ProtocolJump } from '@matter_extractor/shared';

/**
 * The GEOMETRY of the jumps drawn on the frise (refonte modulaire §7.1;
 * maquette `editeur-protocole.html`, `drawJumps`).
 *
 * §7.1 fixes the drawing in one sentence: « ses sorties au-dessus, en traits
 * droits par couloirs, le saut le plus long partant le plus près du centre de la
 * carte et montant le plus haut, ce qui interdit tout croisement d'un même
 * côté ; ses arrivées en dessous, en pointillé ». That is a rule about numbers,
 * not about the pixels of a particular browser, and it is the only part of the
 * frise a test can pin down: jsdom has no layout, so every card measures zero
 * there and a coordinate asserted in a rendering test would assert nothing.
 * Hence this module - the lanes are computed from boxes handed in, and
 * `protocol-lanes.test.ts` hands in the boxes a browser would report.
 *
 * ## Why the longest jump takes the highest lane
 *
 * On one side, the jumps are sorted by how far their target sits. The farthest
 * gets the greatest height AND the anchor nearest the card's centre, so its
 * corridor encloses the shorter ones instead of cutting across them. Sorted the
 * other way round, two jumps leaving the same card would cross exactly once,
 * every time, and an operator would read a crossing as a meaning.
 *
 * ## What is NOT drawn
 *
 * A jump whose either end has no measured box is skipped rather than drawn at
 * the origin: a lane anchored at (0, 0) is a line across the whole frise that
 * points at nothing. `protocolJumps` has already dropped the jumps whose target
 * step no longer exists (those are a refusal, §7.1), so what is skipped here is
 * only what the DOM has not measured yet - one frame at most.
 */

/** Where one step's card sits, in the track's own coordinates. */
export type LaneBox = {
  /** The horizontal centre of the card: where a lane meets the other end. */
  readonly cx: number;
  /** Its top edge, which outgoing lanes leave from and arrive at. */
  readonly top: number;
  /** Its bottom edge, which incoming lanes leave from and arrive at. */
  readonly bottom: number;
};

/** Which side of the selected card a lane runs along. */
export type LaneSide = 'left' | 'right';

/** One drawn jump: the corridor, its arrowhead, and where its label sits. */
export type Lane = {
  readonly jump: ProtocolJump;
  /** The corridor itself: out of one edge, along the lane, into the other edge. */
  readonly d: string;
  /** The arrowhead, a small triangle closed on the edge the jump LANDS on. */
  readonly tip: string;
  readonly label: { readonly x: number; readonly y: number; readonly anchor: 'start' | 'end' };
  readonly side: LaneSide;
};

export type LaneLayoutInput = {
  /** The steps in the order the frise lays them out - what « plus loin » means. */
  readonly order: readonly string[];
  /** One entry per MEASURED card; a missing one skips its lanes. */
  readonly boxes: Readonly<Record<string, LaneBox>>;
  /** The only step whose jumps are drawn (§7.1). */
  readonly selectedId: string;
  readonly jumps: readonly ProtocolJump[];
};

/**
 * The numbers of the mockup, kept as named constants because two of them carry
 * the no-crossing rule: lanes are `LANE_GAP` apart and anchors `ANCHOR_GAP`
 * apart, and both are read against the RANK of a jump on its side.
 */
const FIRST_LANE = 14;
const LANE_GAP = 15;
const FIRST_ANCHOR = 6;
const ANCHOR_GAP = 8;
const TIP_HALF = 4;
const TIP_LENGTH = 7;
const LABEL_OFFSET = 8;
const LABEL_LIFT = 4;

/**
 * Two decimals at most in a path.
 *
 * A measured centre is `offsetLeft + offsetWidth / 2`, so half of them are
 * halves, and the raw float would put fifteen digits in an attribute a human
 * reads in the inspector - and in a test's expected string.
 */
function round(value: number): number {
  return Math.round(value * 100) / 100;
}

/** A jump paired with the RANK of its other end, so the sorts read as sorts. */
type RankedJump = { readonly jump: ProtocolJump; readonly rank: number };

/**
 * The jumps of one list whose other end the frise actually lays out.
 *
 * A step absent from `order` has no rank, so « plus loin » is undefined for it
 * and it cannot be placed on a side at all.
 */
function ranked(
  jumps: readonly ProtocolJump[],
  incoming: boolean,
  ranks: ReadonlyMap<string, number>,
): RankedJump[] {
  return jumps.flatMap((jump) => {
    const rank = ranks.get(incoming ? jump.from : jump.to);
    return rank === undefined ? [] : [{ jump, rank }];
  });
}

/**
 * One side's corridors, in the order they were sorted: index 0 is the nearest
 * target, which takes the lowest lane and the outermost anchor.
 *
 * `sorted.length` and not the number of drawn lanes sets the anchor spread: a
 * jump skipped for want of a box leaves its slot empty rather than sliding
 * every other lane sideways for one frame.
 */
function corridors(params: {
  readonly sorted: readonly RankedJump[];
  readonly boxes: Readonly<Record<string, LaneBox>>;
  readonly selected: LaneBox;
  readonly direction: 1 | -1;
  readonly incoming: boolean;
}): Lane[] {
  const { sorted, boxes, selected, direction, incoming } = params;
  const lanes: Lane[] = [];
  sorted.forEach(({ jump }, index) => {
    const other = boxes[incoming ? jump.from : jump.to];
    if (other === undefined) return;

    const edge = incoming ? selected.bottom : selected.top;
    const laneY = incoming
      ? edge + FIRST_LANE + LANE_GAP * index
      : edge - FIRST_LANE - LANE_GAP * index;
    const anchorX =
      selected.cx + direction * (FIRST_ANCHOR + ANCHOR_GAP * (sorted.length - 1 - index));
    const otherEdge = incoming ? other.bottom : other.top;

    /*
      The corridor always leaves an edge, runs along its lane and comes back to
      an edge - four segments, never a curve. §7.1 says « en traits droits par
      couloirs », and a curve would make two lanes look tangent where they are
      15 px apart.
    */
    const d = incoming
      ? `M${round(other.cx)} ${round(otherEdge)} V${round(laneY)} H${round(anchorX)} V${round(edge)}`
      : `M${round(anchorX)} ${round(edge)} V${round(laneY)} H${round(other.cx)} V${round(otherEdge)}`;
    const tip = incoming
      ? `M${round(anchorX)} ${round(edge)} l-${TIP_HALF} ${TIP_LENGTH} l${TIP_HALF * 2} 0 z`
      : `M${round(other.cx)} ${round(otherEdge)} l-${TIP_HALF} -${TIP_LENGTH} l${TIP_HALF * 2} 0 z`;

    /*
      The label hangs off the end of the lane that NAMES it: an outgoing jump is
      named where it leaves (the condition belongs to the selected step), an
      incoming one where it comes from. Anchored away from the card on both
      sides, so two opposite lanes never write over each other.
    */
    const labelX = (incoming ? other.cx : anchorX) + direction * LABEL_OFFSET;
    lanes.push({
      jump,
      d,
      tip,
      label: {
        x: round(labelX),
        y: round(laneY - LABEL_LIFT),
        anchor: direction > 0 ? 'start' : 'end',
      },
      side: direction > 0 ? 'right' : 'left',
    });
  });
  return lanes;
}

/**
 * The lanes of the selected step: what LEAVES it, above, and what REACHES it,
 * below (§7.1).
 *
 * A jump from the selected step onto itself is on neither side - its rank
 * equals the selected one - and stays undrawn, which is what the mockup does
 * too: a corridor from a card to itself has no width to run in.
 */
export function laneLayout({ order, boxes, selectedId, jumps }: LaneLayoutInput): {
  outgoing: Lane[];
  incoming: Lane[];
} {
  const selected = boxes[selectedId];
  const ranks = new Map(order.map((id, index) => [id, index] as const));
  const selectedRank = ranks.get(selectedId);
  if (selected === undefined || selectedRank === undefined) return { outgoing: [], incoming: [] };

  const leaving = ranked(
    jumps.filter((jump) => jump.from === selectedId),
    false,
    ranks,
  );
  const arriving = ranked(
    jumps.filter((jump) => jump.to === selectedId),
    true,
    ranks,
  );

  /* Nearest first on each side, so index 0 is always the shortest jump. */
  const further = (side: readonly RankedJump[]): RankedJump[] =>
    side.filter((one) => one.rank > selectedRank).sort((a, b) => a.rank - b.rank);
  const earlier = (side: readonly RankedJump[]): RankedJump[] =>
    side.filter((one) => one.rank < selectedRank).sort((a, b) => b.rank - a.rank);

  const shared = { boxes, selected } as const;
  return {
    outgoing: [
      ...corridors({ ...shared, sorted: further(leaving), direction: 1, incoming: false }),
      ...corridors({ ...shared, sorted: earlier(leaving), direction: -1, incoming: false }),
    ],
    incoming: [
      ...corridors({ ...shared, sorted: earlier(arriving), direction: -1, incoming: true }),
      ...corridors({ ...shared, sorted: further(arriving), direction: 1, incoming: true }),
    ],
  };
}

/**
 * The key a lane is identified by across a render: its two ends and what it
 * comes from.
 *
 * Two conditions of one step may aim at the SAME step - a different trigger,
 * the same target - so the pair of ids is not unique, and a React key that
 * collides would make two corridors share one hover state.
 */
export function jumpKey(jump: ProtocolJump): string {
  const source = jump.source.kind === 'fallback' ? 'repli' : jump.source.conditionId;
  return `${jump.from}>${jump.to}>${source}`;
}
